<template>
  <Transition name="fade">
    <div v-if="showing" class="pqb-overlay">
      <div class="pqb-modal">

        <!-- HEADER -->
        <div class="pqb-header">
          <div class="pqb-header-left">
            <div class="pqb-header-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zM6 20V4h7v5h5v11H6z"/>
                <path d="M8 14h8v2H8zm0-4h5v2H8z"/>
              </svg>
            </div>
            <div>
              <h2 class="pqb-title">Constructor de ficha</h2>
              <p class="pqb-subtitle">{{ questions.length }} {{ questions.length === 1 ? 'elemento' : 'elementos' }} — arrastra tipos al area derecha o haz clic en ellos</p>
            </div>
          </div>
          <button class="pqb-close-btn" @click.prevent="close" aria-label="Cerrar">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="pqb-close-icon">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <!-- BODY -->
        <div class="pqb-body">

          <!-- SIDEBAR -->
          <aside class="pqb-sidebar">
            <p class="pqb-sidebar-label">Tipos de elemento</p>
            <p class="pqb-sidebar-hint">Arrastra o haz clic para agregar</p>

            <draggable
              tag="div"
              class="pqb-palette"
              :list="toolItems"
              :group="{ name: 'palette', pull: 'clone', put: false }"
              :sort="false"
              :clone="cloneQuestion"
            >
              <div
                v-for="tool in toolItems"
                :key="tool.type"
                class="pqb-tool-btn"
                :class="'pqb-tool-' + tool.type"
                @click="addQuestion(tool.type)"
              >
                <span class="pqb-tool-icon" v-html="tool.icon"></span>
                <span class="pqb-tool-text">
                  <strong>{{ tool.label }}</strong>
                  <small>{{ tool.description }}</small>
                </span>
                <span class="pqb-tool-drag-hint" title="Arrastra para agregar">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" fill="currentColor"><path d="M360-160q-33 0-56.5-23.5T280-240q0-33 23.5-56.5T360-320q33 0 56.5 23.5T440-240q0 33-23.5 56.5T360-160Zm240 0q-33 0-56.5-23.5T520-240q0-33 23.5-56.5T600-320q33 0 56.5 23.5T680-240q0 33-23.5 56.5T600-160ZM360-400q-33 0-56.5-23.5T280-480q0-33 23.5-56.5T360-560q33 0 56.5 23.5T440-480q0 33-23.5 56.5T360-400Zm240 0q-33 0-56.5-23.5T520-480q0-33 23.5-56.5T600-560q33 0 56.5 23.5T680-480q0 33-23.5 56.5T600-400ZM360-640q-33 0-56.5-23.5T280-720q0-33 23.5-56.5T360-800q33 0 56.5 23.5T440-720q0 33-23.5 56.5T360-640Zm240 0q-33 0-56.5-23.5T520-720q0-33 23.5-56.5T600-800q33 0 56.5 23.5T680-720q0 33-23.5 56.5T600-640Z"/></svg>
                </span>
              </div>
            </draggable>

            <p v-if="maximumQuestionError" class="pqb-max-error">
              Maximo {{ maxQuestions }} elementos permitidos.
            </p>
          </aside>

          <!-- CONTENT -->
          <main class="pqb-content" ref="contentArea">

            <!-- Empty / drop zone -->
            <div v-if="questions.length === 0" class="pqb-empty-state">
              <draggable
                tag="div"
                class="pqb-drop-zone"
                :list="questions"
                :group="{ name: 'palette', pull: false, put: true }"
                @add="onDropAdd"
              >
                <div class="pqb-drop-zone-inner">
                  <div class="pqb-empty-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                    </svg>
                  </div>
                  <h3>Arrastra aqui o haz clic en un tipo</h3>
                  <p>La ficha esta vacia. Arrastra un elemento desde el panel izquierdo o haz clic en el para comenzar.</p>
                </div>
              </draggable>
            </div>

            <!-- Questions list -->
            <draggable
              v-else
              tag="div"
              :list="questions"
              class="pqb-questions-list"
              :group="{ name: 'palette', pull: false, put: true }"
              handle=".pqb-drag-handle"
              @end="onDragEnd"
              @add="onDropAdd"
            >
              <div
                v-for="(question, index) in questions"
                :key="'q' + question.id_pregunta + '_' + index"
                class="pqb-question-wrapper"
                v-show="!hiddenItems[index]"
                :style="nestedStatus[index] ? 'margin-left: 32px; border-left: 2px solid #e2e8f0; padding-left: 8px;' : ''"
              >
                <div class="pqb-drag-handle" title="Arrastrar para reordenar">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" fill="currentColor">
                    <path d="M120-200v-80h720v80H120Zm0-160v-80h720v80H120Zm0-160v-80h720v80H120Zm0-160v-80h720v80H120Z"/>
                  </svg>
                </div>

                <div class="pqb-question-card" :class="{ 'pqb-question-card--error': question.error }">
                  <!-- Card header -->
                  <div class="pqb-card-header" @click="toggleCollapse(index)">
                    <div class="pqb-card-header-left">
                      <span class="pqb-card-badge" :class="getBadgeClass(question.tipo)">
                        {{ getTypeLabel(question.tipo) }}
                      </span>
                      <span class="pqb-card-num">{{ question.numero_pregunta || index + 1 }}.</span>
                      <span class="pqb-card-preview">
                        {{ question.texto || 'Sin texto...' }}
                      </span>
                    </div>
                    <div class="pqb-card-header-right">
                      <button v-if="question.tipo === 'SECCION'" type="button" class="pqb-card-delete-btn" title="Ocultar/Mostrar contenido de la sección" @click.stop="toggleSectionContents(index)" style="color: #6366f1; background: #e0e7ff; margin-right: 4px; border-color: #c7d2fe;">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                          <!-- Eye icon when visible -->
                          <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" v-if="!hiddenItems[index+1]"/>
                          <!-- Eye-off icon when hidden -->
                          <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" v-else/>
                        </svg>
                      </button>
                      <button type="button" class="pqb-card-delete-btn" title="Eliminar" @click.stop="removeQuestion(index)">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                        </svg>
                      </button>
                      <button class="pqb-close-btn" @click.stop="toggleCollapse(index)">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="pqb-close-icon"
                             style="transition: transform 0.2s;"
                             :class="{ 'pqb-chevron-up': !collapsedItems[index] }">
                          <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                        </svg>
                      </button>
                    </div>
                  </div>

                  <!-- Card body -->
                  <transition name="collapse">
                    <div v-if="!collapsedItems[index]" class="pqb-card-body">
                      <open-question v-if="question.tipo === 'ABIERTA'" @click="removeQuestion(index)" :question="question" :ref="'question' + index"></open-question>
                      <multiple-question v-if="question.tipo === 'SELECCION'" @click="removeQuestion(index)" :question="question" :all-questions="questions" :ref="'question' + index"></multiple-question>
                      <multiselect-question v-if="question.tipo === 'MULTISELECCION'" @click="removeQuestion(index)" :question="question" :ref="'question' + index"></multiselect-question>
                      <conditional-question v-if="question.tipo === 'SELECCION_CONDICIONAL'" @click="removeQuestion(index)" :question="question" :ref="'question' + index"></conditional-question>
                      <fields-question v-if="question.tipo === 'CAMPOS'" @click="removeQuestion(index)" :question="question" :ref="'question' + index"></fields-question>
                      <date-question v-if="question.tipo === 'FECHA'" @click="removeQuestion(index)" :question="question" :ref="'question' + index"></date-question>
                      <matrix-question v-if="question.tipo === 'MATRIZ'" @click="removeQuestion(index)" :question="question" :ref="'question' + index"></matrix-question>
                      <section-question v-if="question.tipo === 'SECCION'" @click="removeQuestion(index)" :question="question" :ref="'question' + index"></section-question>
                      <photo-question v-if="question.tipo === 'FOTO'" @click="removeQuestion(index)" :question="question" :ref="'question' + index"></photo-question>
                      <repeatable-group-question v-if="question.tipo === 'GRUPO_REPETIBLE'" @click="removeQuestion(index)" :question="question" :ref="'question' + index"></repeatable-group-question>
                      <binary-question v-if="question.tipo === 'OPCION_SEGUIMIENTO'" @click="removeQuestion(index)" :question="question" :ref="'question' + index"></binary-question>
                      <mini-form-question v-if="question.tipo === 'MINI_FORMULARIO'" @click="removeQuestion(index)" :question="question" :ref="'question' + index"></mini-form-question>
                    </div>
                  </transition>
                </div>
              </div>
            </draggable>
          </main>
        </div>

        <!-- FOOTER -->
        <div class="pqb-footer">
          <span class="pqb-footer-counter">{{ questions.length }} / {{ maxQuestions }} elementos</span>
          <div class="pqb-footer-actions">
            <!-- Input file oculto para importar JSON -->
            <input
              ref="jsonFileInput"
              type="file"
              accept=".json,application/json"
              style="display:none"
              @change="onJsonFileSelected"
            />
            <button class="pqb-btn pqb-btn-secondary" @click="$refs.jsonFileInput.click()">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="pqb-btn-icon">
                <path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z"/>
              </svg>
              Importar JSON
            </button>
            <button class="pqb-btn pqb-btn-secondary" @click="showJsonModal = true">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="pqb-btn-icon">
                <path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/>
              </svg>
              Ver JSON
            </button>
            <button class="pqb-btn pqb-btn-secondary" @click="showPreviewQuestions()">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="pqb-btn-icon">
                <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
              </svg>
              Previsualizar
            </button>
            <button class="pqb-btn pqb-btn-primary" :style="{ backgroundColor: colorT }" @click="saveQuestions()">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="pqb-btn-icon">
                <path d="M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z"/>
              </svg>
              Guardar ficha
            </button>
          </div>
        </div>

        <project-preview-question-builder
          :showing="showProjectPreview"
          @close="showProjectPreview = false"
          :colorP="colorP" :colorS="colorS" :colorT="colorT"
        ></project-preview-question-builder>

        <!-- JSON Modal (Ver) -->
        <div v-if="showJsonModal" class="pqb-json-overlay" @click.self="showJsonModal = false">
          <div class="pqb-json-modal">
            <div class="pqb-json-header">
              <h3 style="margin:0;font-size:1rem;color:#f8fafc">Estructura JSON</h3>
              <button class="pqb-close-btn" @click="showJsonModal = false">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>
            <div class="pqb-json-content">
              <pre v-html="formattedJson"></pre>
            </div>
          </div>
        </div>

        <!-- Import JSON Modal -->
        <div v-if="showImportModal" class="pqb-json-overlay" @click.self="cancelImport">
          <div class="pqb-json-modal pqb-import-modal">
            <div class="pqb-json-header">
              <div style="display:flex;align-items:center;gap:10px">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20" style="color:#818cf8">
                  <path d="M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z"/>
                </svg>
                <span style="font-size:1rem;color:#f8fafc;font-weight:600">Importar JSON</span>
              </div>
              <button class="pqb-close-btn" @click="cancelImport">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>

            <!-- Error de parseo -->
            <div v-if="importError" class="pqb-import-error">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
              {{ importError }}
            </div>

            <!-- Info de preguntas encontradas -->
            <div v-if="importedQuestions.length > 0 && !importError" class="pqb-import-info">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18" style="color:#34d399">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
              </svg>
              Se encontraron <strong>{{ importedQuestions.length }}</strong> pregunta(s) válidas en el archivo.
            </div>

            <!-- Preview del JSON -->
            <div class="pqb-json-content pqb-import-preview">
              <pre v-html="importedFormattedJson"></pre>
            </div>

            <!-- Opciones de importación -->
            <div v-if="importedQuestions.length > 0 && !importError" class="pqb-import-options">
              <label class="pqb-import-radio">
                <input type="radio" v-model="importMode" value="replace" />
                <span>Reemplazar preguntas actuales</span>
              </label>
              <label class="pqb-import-radio">
                <input type="radio" v-model="importMode" value="append" />
                <span>Agregar al final ({{ questions.length }} existentes + {{ importedQuestions.length }} nuevas)</span>
              </label>
            </div>

            <!-- Acciones -->
            <div class="pqb-import-footer">
              <button class="pqb-btn pqb-btn-secondary" @click="cancelImport">Cancelar</button>
              <button
                v-if="importedQuestions.length > 0 && !importError"
                class="pqb-btn pqb-btn-primary"
                :style="{ backgroundColor: colorT }"
                @click="confirmImport"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="pqb-btn-icon">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                </svg>
                Confirmar importación
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
import OpenQuestion from "./OpenQuestion";
import MultipleQuestion from "./MultipleQuestion";
import MultiselectQuestion from "./MultiselectQuestion";
import ConditionalQuestion from "./ConditionalQuestion";
import FieldsQuestion from "./FieldsQuestion";
import ProjectPreviewQuestionBuilder from "./ProjectPreviewQuestionBuilder";
import EventBus from "../event-bus";
import draggable from "vuedraggable";
import DateQuestion from "./DateQuestion";
import MatrixQuestion from "./MatrixQuestion";
import SectionQuestion from "./SectionQuestion";
import PhotoQuestion from "./PhotoQuestion";
import RepeatableGroupQuestion from "./RepeatableGroupQuestion";
import BinaryQuestion from "./BinaryQuestion";
import MiniFormQuestion from "./MiniFormQuestion";

const TYPE_CONFIG = {
  ABIERTA:              { label: "Abierta",      badge: "badge-open" },
  SELECCION:            { label: "Opcion",        badge: "badge-multiple" },
  MULTISELECCION:       { label: "Multiple",      badge: "badge-multi" },
  SELECCION_CONDICIONAL:{ label: "Condicional",   badge: "badge-cond" },
  CAMPOS:               { label: "Campos",        badge: "badge-fields" },
  MATRIZ:               { label: "Matriz",        badge: "badge-matrix" },
  SECCION:              { label: "Seccion",        badge: "badge-section" },
  FECHA:                { label: "Fecha",          badge: "badge-date" },
  FOTO:                 { label: "Foto",           badge: "badge-photo" },
  GRUPO_REPETIBLE:      { label: "Grupo Rep.",     badge: "badge-group" },
  OPCION_SEGUIMIENTO:   { label: "Seguimiento",    badge: "badge-followup" },
  MINI_FORMULARIO:      { label: "Mini Form",      badge: "badge-miniform" },
};

const TOOL_ITEMS = [
  { type: "open",        label: "Pregunta abierta",      description: "Texto libre",       icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M4 9h16v2H4zm0 4h10v2H4z"/></svg>' },
  { type: "multiple",    label: "Opcion unica",           description: "Una respuesta",     icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zm0-5C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/></svg>' },
  { type: "multiselect", label: "Seleccion multiple",     description: "Varias respuestas", icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="m10.933 13.519-2.226-2.226-1.414 1.414 3.774 3.774 5.702-6.84-1.538-1.282z"/><path d="M19 3H5c-1.103 0-2 .897-2 2v14c0 1.103.897 2 2 2h14c1.103 0 2-.897 2-2V5c0-1.103-.897-2-2-2zM5 19V5h14l.002 14H5z"/></svg>' },
  { type: "conditional", label: "Condicional",            description: "Logica ramificada", icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M7.45 17.45 6.05 16.05 9.075 13H2V11H9.075L6.05 7.95L7.45 6.55L12.9 12ZM13 9V7H22V9ZM13 17V15H22V17ZM16 13V11H22V13Z"/></svg>' },
  { type: "fields",      label: "Campos",                 description: "Multiples campos",  icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M7 20V7H2V4H15V7H10V20ZM16 20V12H13V9H22V12H19V20Z"/></svg>' },
  { type: "matrix",      label: "Matriz",                 description: "Tabla de opciones", icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16v16H4z"/><path d="M4 10h16M4 16h16M10 4v16M16 4v16"/></svg>' },
  { type: "section",     label: "Divisor de seccion",     description: "Agrupa elementos",  icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" fill="currentColor"><path d="M160-200v-80h640v80H160Zm0-240v-80h640v80H160Zm0-240v-80h640v80H160Z"/></svg>' },
  { type: "photo",       label: "Foto",                   description: "Subir imagen",      icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>' },
  { type: "repeatableGroup", label: "Grupo Repetible",    description: "Multiples repetibles", icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-1 9h-4v4h-2v-4H9V9h4V5h2v4h4v2z"/></svg>' },
  { type: "followup", label: "Opcion con seguimiento", description: "2 opciones + subpregunta", icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M17 12h-5v5h5v-5zM16 1v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-1V1h-2zm3 18H5V8h14v11z"/></svg>' },
  { type: "miniform", label: "Mini Formulario", description: "Varios inputs", icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M4 3h16a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zm1 2v14h14V5H5zm2 2h10v2H7V7zm0 4h10v2H7v-2zm0 4h7v2H7v-2z"/></svg>' },
];

export default {
  name: "project-quest-builder",
  data() {
    return {
      lastId: 1,
      backupQuestions: [],
      maximumQuestionError: false,
      showProjectPreview: false,
      maxQuestions: 65,
      collapsedItems: {},
      hiddenItems: {},
      toolItems: TOOL_ITEMS,
      showJsonModal: false,
      // Importar JSON
      showImportModal: false,
      importedQuestions: [],
      importError: "",
      importMode: "replace",
    };
  },
  props: {
    showing: { required: true, type: Boolean },
    questions: { required: true, type: Array },
    modalAction: { required: true },
    colorP: { required: true },
    colorS: { required: true },
    colorT: { required: true },
  },
  components: {
    OpenQuestion, MultipleQuestion, MultiselectQuestion, ConditionalQuestion,
    FieldsQuestion, ProjectPreviewQuestionBuilder, draggable,
    DateQuestion, MatrixQuestion, SectionQuestion, PhotoQuestion, RepeatableGroupQuestion,
    BinaryQuestion, MiniFormQuestion
  },
  watch: {
    showing(value) {
      if (value) {
        this.lastId = this.questions.length + 1;
        this.updateQuestionNumbers();
        document.querySelector("body").classList.add("overflow-hidden");
        return;
      }
      document.querySelector("body").classList.remove("overflow-hidden");
    },
  },
  mounted() {
    this.backupQuestions = JSON.parse(JSON.stringify(this.questions));
    this.fetchConfiguration();
  },
  methods: {
    fetchConfiguration() {
      this.axios.get("apidev/api/config/site").then(r => {
        if (r.data && r.data.config && typeof r.data.config.max_project_questions === "number") {
          this.maxQuestions = r.data.config.max_project_questions;
        }
      }).catch(() => {});
    },
    getTypeLabel(tipo) { return TYPE_CONFIG[tipo] ? TYPE_CONFIG[tipo].label : tipo; },
    getBadgeClass(tipo) { return TYPE_CONFIG[tipo] ? TYPE_CONFIG[tipo].badge : ""; },
    toggleCollapse(index) {
      this.$set(this.collapsedItems, index, !this.collapsedItems[index]);
    },
    toggleSectionContents(index) {
      let isCollapsed = false;
      if (index + 1 < this.questions.length) {
        isCollapsed = !this.hiddenItems[index + 1];
      }
      for (let i = index + 1; i < this.questions.length; i++) {
        if (this.questions[i].tipo === 'SECCION') break;
        this.$set(this.hiddenItems, i, isCollapsed);
      }
    },
    reset() { this.lastId = 1; this.showProjectPreview = false; this.collapsedItems = {}; this.hiddenItems = {}; },
    close() {
      if (this.modalAction !== "edit") this.reset();
      this.$emit("close", this.backupQuestions);
    },
    createQuestion(type) {
      const base = { id_pregunta: this.lastId, texto: "", nota_para_registrante: "", obligatoria: false, error: false };
      const templates = {
        open:        { ...base, tipo: "ABIERTA",              tipo_dato: "texto", rango_min: null, rango_max: null, subpregunta: [] },
        multiple:    { ...base, tipo: "SELECCION",            opciones: ["", ""], saltos: {}, otro: false, subpregunta: [], optionError: false, minimumError: false, maximumOptionError: false, maximumSubquestionError: false },
        multiselect: { ...base, tipo: "MULTISELECCION",       opciones: ["", ""], otro: false, subpregunta: [], optionError: false, minimumError: false, maximumOptionError: false },
        conditional: { ...base, tipo: "SELECCION_CONDICIONAL",opciones: ["", ""], preguntas_condicion: [], errorCond: false, optionError: false, minimumError: false, maximumError: false, duplicateError: false, maximumOptionError: false },
        fields:      { ...base, tipo: "CAMPOS",               campos: [{ texto: "", tipo: "texto", rango_min: null, rango_max: null }], fieldError: false, minimumError: false, maximumOptionError: false },
        matrix:      { ...base, tipo: "MATRIZ",               filas: [""], columnas: ["", ""], obligatoria: true, otro: false, filaError: false, columnaError: false, maximumFilaError: false, maximumColumnaError: false },
        section:     { ...base, tipo: "SECCION" },
        date:        { ...base, tipo: "FECHA" },
        photo:       { ...base, tipo: "FOTO", max_fotos: 4 },
        repeatableGroup: { ...base, tipo: "GRUPO_REPETIBLE",  campos: [{ texto: "", tipo: "texto", rango_min: null, rango_max: null, opciones: [], opciones_texto: "" }], fieldError: false, minimumError: false, maximumOptionError: false },
        followup: { ...base, tipo: "OPCION_SEGUIMIENTO", conceptos: ["", ""], conceptoError: false, conceptoMinError: false, opcion_si: "Sí", opcion_no: "No", sub_tipo: null, sub_opciones: ["", ""], sub_disposicion: "horizontal", sub_input_tipo: "texto", sub_rango_min: null, sub_rango_max: null, sub_label: "", subTipoError: false, subOptionError: false, subMinError: false },
        miniform: { ...base, tipo: "MINI_FORMULARIO", campos: [{ texto: "", tipo: "texto", rango_min: null, rango_max: null, opciones: [], opciones_texto: "" }], fieldError: false, minimumError: false, maximumOptionError: false },
      };
      return templates[type] || null;
    },
    cloneQuestion(toolItem) {
      return this.createQuestion(toolItem.type);
    },
    onDropAdd(evt) {
      this.lastId = this.questions.length + 1;
      this.questions.forEach((q, i) => { if (!q.id_pregunta || q.id_pregunta === undefined) q.id_pregunta = i + 1; });
      this.updateQuestionNumbers();
      const newIndex = evt.newIndex !== undefined ? evt.newIndex : this.questions.length - 1;
      // Colapsar todas las preguntas existentes y dejar solo la nueva expandida
      const newCollapsed = {};
      this.questions.forEach((_, i) => { newCollapsed[i] = i !== newIndex; });
      this.collapsedItems = newCollapsed;
      this.$nextTick(() => { this.updateQuestionNumbers(); });
    },
    addQuestion(type) {
      if (this.questions.length >= this.maxQuestions) { this.maximumQuestionError = true; return; }
      const q = this.createQuestion(type);
      if (q) {
        const newIndex = this.questions.length;
        this.questions.push(q);
        this.$set(this.collapsedItems, newIndex, false);
        this.lastId += 1;
        this.updateQuestionNumbers();
      }
    },
    removeQuestion(questionKey) {
      this.questions.splice(questionKey, 1);
      const newCollapsed = {};
      Object.keys(this.collapsedItems).forEach(k => {
        const ki = parseInt(k);
        if (ki < questionKey) newCollapsed[ki] = this.collapsedItems[ki];
        else if (ki > questionKey) newCollapsed[ki - 1] = this.collapsedItems[ki];
      });
      this.collapsedItems = newCollapsed;
      this.updateQuestionNumbers();
      this.lastId = this.questions.length + 1;
      this.maximumQuestionError = false;
    },
    saveQuestions() { if (this.validateQuestions()) this.$emit("save"); },
    validateQuestions() {
      let vals = [];
      Object.keys(this.$refs).forEach(el => { if (this.$refs[el][0]) vals.push(this.$refs[el][0].validateQuestion()); });
      return vals.every(Boolean);
    },
    showPreviewQuestions() {
      if (this.validateQuestions()) {
        this.showProjectPreview = true;
        EventBus.$emit("previewQuestions", { questions: JSON.parse(JSON.stringify(this.questions)) });
      }
    },
    onDragEnd(evt) {
      if (evt.newIndex !== evt.oldIndex) {
        const oldC = { ...this.collapsedItems };
        const movedVal = oldC[evt.oldIndex];
        const newC = {};
        const indices = Array.from({ length: this.questions.length }, (_, i) => i);
        indices.splice(evt.oldIndex, 1);
        indices.splice(evt.newIndex, 0, evt.oldIndex);
        indices.forEach((orig, dest) => { newC[dest] = orig === evt.oldIndex ? movedVal : oldC[orig]; });
        this.collapsedItems = newC;
        this.updateQuestionNumbers();
      }
    },
    updateQuestionNumbers() {
      let sectionIndex = 0, questionIndex = 1, globalIndex = 1;
      this.questions.forEach((question, index) => {
        question.id_pregunta = index + 1;
        if (question.tipo === "SECCION") {
          sectionIndex++; questionIndex = 1;
          this.$set(question, "numero_pregunta", sectionIndex.toString());
        } else {
          if (sectionIndex === 0) this.$set(question, "numero_pregunta", globalIndex.toString());
          else { this.$set(question, "numero_pregunta", `${sectionIndex}.${questionIndex}`); questionIndex++; }
        }
        globalIndex++;
      });
    },
    onJsonFileSelected(event) {
      const file = event.target.files[0];
      if (!file) return;
      // Reset el input para poder volver a seleccionar el mismo archivo
      this.$refs.jsonFileInput.value = "";
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const parsed = JSON.parse(e.target.result);
          // Aceptar tanto array directo como objeto con propiedad 'preguntas' o 'questions'
          let arr = null;
          if (Array.isArray(parsed)) {
            arr = parsed;
          } else if (Array.isArray(parsed.preguntas)) {
            arr = parsed.preguntas;
          } else if (Array.isArray(parsed.questions)) {
            arr = parsed.questions;
          }
          if (!arr) {
            this.importError = "El JSON no contiene un arreglo de preguntas válido. Debe ser un array o un objeto con la propiedad 'preguntas'/'questions'.";
            this.importedQuestions = [];
          } else if (arr.length === 0) {
            this.importError = "El archivo no contiene preguntas.";
            this.importedQuestions = [];
          } else {
            this.importError = "";
            this.importedQuestions = arr;
          }
        } catch (err) {
          this.importError = "Error al parsear el JSON: " + err.message;
          this.importedQuestions = [];
        }
        this.importMode = "replace";
        this.showImportModal = true;
      };
      reader.readAsText(file);
    },
    cancelImport() {
      this.showImportModal = false;
      this.importedQuestions = [];
      this.importError = "";
    },
    confirmImport() {
      const incoming = JSON.parse(JSON.stringify(this.importedQuestions));
      if (this.importMode === "replace") {
        this.questions.splice(0, this.questions.length, ...incoming);
      } else {
        this.questions.push(...incoming);
      }
      this.updateQuestionNumbers();
      this.lastId = this.questions.length + 1;
      this.collapsedItems = {};
      this.hiddenItems = {};
      this.questions.forEach((_, i) => this.$set(this.collapsedItems, i, true));
      this.cancelImport();
    },
    colorizeJson(json) {
      return json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g, function (match) {
          let cls = 'color:#f87171';
          if (/^"|/.test(match)) {
            if (/:$/.test(match)) { cls = 'color:#c0caf5'; }
            else { cls = 'color:#9ece6a'; }
          } else if (/true|false/.test(match)) { cls = 'color:#bb9af7'; }
          else if (/null/.test(match)) { cls = 'color:#565f89'; }
          return '<span style="' + cls + '">' + match + '</span>';
        });
    },
  },
  computed: {
    nestedStatus() {
      let inSection = false;
      if (!this.questions) return [];
      return this.questions.map(q => {
        if (q.tipo === 'SECCION') {
          inSection = true;
          return false;
        }
        return inSection;
      });
    },
    formattedJson() {
      if (!this.questions) return "";
      const json = JSON.stringify(this.questions, null, 2);
      return this.colorizeJson(json);
    },
    importedFormattedJson() {
      if (!this.importedQuestions || this.importedQuestions.length === 0) {
        return this.importError ? '<span style="color:#f87171">' + this.importError + '</span>' : '';
      }
      const json = JSON.stringify(this.importedQuestions, null, 2);
      return this.colorizeJson(json);
    },
  },
};
</script>

<style scoped>
/* OVERLAY */
.pqb-overlay {
  position: fixed; inset: 0; display: flex; align-items: center; justify-content: center;
  background: rgba(15, 23, 42, 0.8); backdrop-filter: blur(6px); z-index: 200; padding: 12px;
}
/* MODAL — mas grande */
.pqb-modal {
  display: flex; flex-direction: column; width: 100%; max-width: 1100px; height: 90vh;
  background: #f8fafc; border-radius: 18px; box-shadow: 0 32px 80px rgba(0,0,0,0.4); overflow: hidden;
}
/* HEADER */
.pqb-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 28px; background: #fff; border-bottom: 1px solid #e2e8f0; flex-shrink: 0;
}
.pqb-header-left { display: flex; align-items: center; gap: 14px; }
.pqb-header-icon {
  display: flex; align-items: center; justify-content: center; width: 44px; height: 44px;
  background: linear-gradient(135deg, #667eea, #764ba2); border-radius: 10px; color: white; flex-shrink: 0;
}
.pqb-header-icon svg { width: 22px; height: 22px; }
.pqb-title { font-size: 1.2rem; font-weight: 700; color: #1e293b; margin: 0; }
.pqb-subtitle { font-size: 0.75rem; color: #94a3b8; margin: 0; }
.pqb-close-btn {
  display: flex; align-items: center; justify-content: center; width: 36px; height: 36px;
  border-radius: 8px; border: none; background: transparent; color: #94a3b8; cursor: pointer; transition: all 0.15s;
}
.pqb-close-btn:hover { background: #fee2e2; color: #ef4444; }
.pqb-close-icon { width: 20px; height: 20px; }
/* BODY */
.pqb-body { display: flex; flex: 1; overflow: hidden; min-height: 0; }
/* SIDEBAR */
.pqb-sidebar {
  width: 230px; flex-shrink: 0; background: #fff; border-right: 1px solid #e2e8f0;
  padding: 16px 12px 16px 12px; overflow-y: auto; display: flex; flex-direction: column; gap: 4px;
  min-height: 0;
}
.pqb-sidebar-label {
  font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em;
  color: #94a3b8; margin: 0 0 2px 4px;
}
.pqb-sidebar-hint {
  font-size: 0.68rem; color: #c7d2fe; margin: 0 0 10px 4px; font-style: italic;
}
/* Palette draggable wrapper */
.pqb-palette { display: flex; flex-direction: column; gap: 5px; }
/* Tool items */
.pqb-tool-btn {
  display: flex; align-items: center; gap: 9px; width: 100%; padding: 9px 10px;
  border: 1px solid #e2e8f0; border-radius: 10px; background: #fff; cursor: grab;
  transition: all 0.15s; text-align: left; position: relative;
}
.pqb-tool-btn:hover { transform: translateX(2px); box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
.pqb-tool-btn:active { cursor: grabbing; }
.pqb-tool-icon {
  display: flex; align-items: center; justify-content: center;
  width: 30px; height: 30px; border-radius: 8px; flex-shrink: 0;
}
.pqb-tool-icon svg { width: 15px; height: 15px; }
.pqb-tool-text { display: flex; flex-direction: column; gap: 1px; flex: 1; min-width: 0; }
.pqb-tool-text strong { font-size: 0.75rem; font-weight: 600; line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pqb-tool-text small { font-size: 0.65rem; color: #94a3b8; line-height: 1.2; }
.pqb-tool-drag-hint { color: #cbd5e1; flex-shrink: 0; display: flex; }
.pqb-tool-drag-hint svg { width: 14px; height: 14px; }
/* Color variants */
.pqb-tool-open        { color: #0d9488; } .pqb-tool-open .pqb-tool-icon        { background: #ccfbf1; color: #0d9488; }
.pqb-tool-multiple    { color: #6366f1; } .pqb-tool-multiple .pqb-tool-icon    { background: #e0e7ff; color: #6366f1; }
.pqb-tool-multiselect { color: #8b5cf6; } .pqb-tool-multiselect .pqb-tool-icon { background: #ede9fe; color: #8b5cf6; }
.pqb-tool-conditional { color: #f97316; } .pqb-tool-conditional .pqb-tool-icon { background: #ffedd5; color: #f97316; }
.pqb-tool-fields      { color: #ec4899; } .pqb-tool-fields .pqb-tool-icon      { background: #fce7f3; color: #ec4899; }
.pqb-tool-matrix      { color: #3b82f6; } .pqb-tool-matrix .pqb-tool-icon      { background: #dbeafe; color: #3b82f6; }
.pqb-tool-section     { color: #64748b; } .pqb-tool-section .pqb-tool-icon     { background: #f1f5f9; color: #64748b; }
.pqb-tool-photo       { color: #0ea5e9; } .pqb-tool-photo .pqb-tool-icon       { background: #e0f2fe; color: #0ea5e9; }
.pqb-tool-repeatableGroup { color: #14b8a6; } .pqb-tool-repeatableGroup .pqb-tool-icon { background: #ccfbf1; color: #14b8a6; }
.pqb-tool-miniform { color: #d946ef; } .pqb-tool-miniform .pqb-tool-icon { background: #fae8ff; color: #d946ef; }
/* Max error */
.pqb-max-error { font-size: 0.72rem; color: #ef4444; background: #fee2e2; border-radius: 6px; padding: 8px 10px; margin-top: 6px; }
/* CONTENT */
.pqb-content { flex: 1; overflow-y: auto; padding: 20px 24px; background: #f1f5f9; }
/* Drop zone (empty state) */
.pqb-empty-state { height: 100%; min-height: 300px; }
.pqb-drop-zone { height: 100%; min-height: 300px; }
.pqb-drop-zone-inner {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  height: 100%; min-height: 300px; border: 2px dashed #c7d2fe; border-radius: 14px;
  background: rgba(255,255,255,0.6); color: #94a3b8; text-align: center; gap: 12px;
  transition: border-color 0.2s, background 0.2s;
}
.pqb-drop-zone:hover .pqb-drop-zone-inner,
.sortable-drag ~ .pqb-drop-zone-inner { border-color: #818cf8; background: rgba(238,242,255,0.8); }
.pqb-empty-icon {
  width: 56px; height: 56px; background: #e0e7ff; border-radius: 14px;
  display: flex; align-items: center; justify-content: center; color: #6366f1;
}
.pqb-empty-icon svg { width: 28px; height: 28px; }
.pqb-empty-state h3 { font-size: 1rem; font-weight: 600; color: #64748b; margin: 0; }
.pqb-empty-state p { font-size: 0.82rem; max-width: 300px; margin: 0; line-height: 1.5; }
/* Questions list */
.pqb-questions-list { display: flex; flex-direction: column; gap: 10px; min-height: 60px; max-width: 850px; }
/* Question wrapper */
.pqb-question-wrapper { display: flex; align-items: flex-start; gap: 8px; }
.pqb-drag-handle {
  display: flex; align-items: center; justify-content: center; width: 22px; min-width: 22px;
  padding-top: 13px; color: #cbd5e1; cursor: grab; transition: color 0.15s; flex-shrink: 0;
}
.pqb-drag-handle:hover { color: #64748b; }
.pqb-drag-handle svg { width: 15px; height: 15px; }
.pqb-drag-handle:active { cursor: grabbing; }
/* Question card */
.pqb-question-card {
  flex: 1; background: #fff; border: 1px solid #e2e8f0; border-radius: 12px;
  overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.05); transition: box-shadow 0.15s, border-color 0.15s;
}
.pqb-question-card:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
.pqb-question-card--error { border-color: #fca5a5; }
/* Card header */
.pqb-card-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 11px 14px; cursor: pointer; user-select: none;
  transition: background 0.12s; gap: 10px;
}
.pqb-card-header:hover { background: #f8fafc; }
.pqb-card-header-left { display: flex; align-items: center; gap: 8px; flex: 1; min-width: 0; }
.pqb-card-header-right { display: flex; align-items: center; gap: 4px; flex-shrink: 0; }
.pqb-card-badge {
  font-size: 0.62rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em;
  padding: 2px 7px; border-radius: 999px; white-space: nowrap; flex-shrink: 0;
}
.badge-open    { background: #ccfbf1; color: #0d9488; }
.badge-multiple{ background: #e0e7ff; color: #6366f1; }
.badge-multi   { background: #ede9fe; color: #8b5cf6; }
.badge-cond    { background: #ffedd5; color: #f97316; }
.badge-fields  { background: #fce7f3; color: #ec4899; }
.badge-matrix  { background: #dbeafe; color: #3b82f6; }
.badge-section { background: #f1f5f9; color: #64748b; }
.badge-date    { background: #d1fae5; color: #059669; }
.badge-photo   { background: #e0f2fe; color: #0ea5e9; }
.badge-group   { background: #ccfbf1; color: #14b8a6; }
.badge-miniform { background: #fae8ff; color: #d946ef; }
.pqb-card-num { font-size: 0.78rem; font-weight: 700; color: #64748b; flex-shrink: 0; }
.pqb-card-preview { font-size: 0.82rem; color: #334155; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1; min-width: 0; }
.pqb-card-delete-btn {
  display: flex; align-items: center; justify-content: center; width: 26px; height: 26px;
  border: none; border-radius: 6px; background: transparent; color: #94a3b8; cursor: pointer; transition: all 0.15s;
}
.pqb-card-delete-btn:hover { background: #fee2e2; color: #ef4444; }
.pqb-card-delete-btn svg { width: 15px; height: 15px; }
.pqb-card-collapse-btn {
  display: flex; align-items: center; justify-content: center; width: 26px; height: 26px;
  border: none; border-radius: 6px; background: transparent; color: #94a3b8; cursor: pointer;
}
.pqb-card-collapse-btn svg { width: 17px; height: 17px; transition: transform 0.2s ease; }
.pqb-chevron-up { transform: rotate(180deg); }
/* Card body */
.pqb-card-body { padding: 18px 20px; border-top: 1px solid #f1f5f9; }
.pqb-card-body > * { max-width: 760px; }
/* Collapse transition */
.collapse-enter-active, .collapse-leave-active { transition: all 0.2s ease; overflow: hidden; }
.collapse-enter, .collapse-leave-to { max-height: 0; opacity: 0; }
.collapse-enter-to, .collapse-leave { max-height: 2000px; opacity: 1; }
/* Ghost while dragging from sidebar */
.sortable-ghost { opacity: 0.4; border: 2px dashed #818cf8 !important; background: #eef2ff !important; }
.sortable-chosen { box-shadow: 0 8px 25px rgba(99,102,241,0.25) !important; }
/* FOOTER */
.pqb-footer {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 24px; background: #fff; border-top: 1px solid #e2e8f0; flex-shrink: 0;
}
.pqb-footer-counter { font-size: 0.8rem; color: #94a3b8; }
.pqb-footer-actions { display: flex; gap: 10px; }
.pqb-btn {
  display: inline-flex; align-items: center; gap: 8px; padding: 10px 20px;
  border: none; border-radius: 10px; font-size: 0.875rem; font-weight: 600; cursor: pointer; transition: all 0.15s;
}
.pqb-btn-icon { width: 16px; height: 16px; }
.pqb-btn-secondary { background: #f1f5f9; color: #475569; }
.pqb-btn-secondary:hover { background: #e2e8f0; }
.pqb-btn-primary { color: #fff; }
.pqb-btn-primary:hover { filter: brightness(0.9); transform: translateY(-1px); }
/* Transitions */
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }
/* JSON Modal */
.pqb-json-overlay { position: absolute; inset: 0; background: rgba(255,255,255,0.7); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 100; border-radius: 18px; }
.pqb-json-modal { background: #1e293b; width: 80%; max-width: 800px; height: 80%; border-radius: 12px; box-shadow: 0 20px 40px rgba(0,0,0,0.2); display: flex; flex-direction: column; overflow: hidden; border: 1px solid #334155; }
.pqb-import-modal { height: auto; max-height: 90%; min-height: 300px; }
.pqb-json-header { display: flex; justify-content: space-between; align-items: center; padding: 12px 20px; border-bottom: 1px solid #334155; background: #0f172a; color: #f8fafc; }
.pqb-json-content { flex: 1; overflow: auto; padding: 20px; background: #1e293b; text-align: left; }
.pqb-json-content pre { margin: 0; color: #a5b4fc; font-size: 0.85rem; font-family: monospace; white-space: pre; overflow-x: auto; text-align: left; }
.pqb-import-preview { max-height: 280px; flex: none; }
.pqb-import-error { display: flex; align-items: center; gap: 8px; padding: 10px 20px; background: #3b1a1a; color: #f87171; font-size: 0.82rem; border-top: 1px solid #4b2020; }
.pqb-import-info { display: flex; align-items: center; gap: 8px; padding: 10px 20px; background: #0f2d22; color: #86efac; font-size: 0.82rem; border-top: 1px solid #14532d; }
.pqb-import-info strong { color: #34d399; }
.pqb-import-options { display: flex; flex-direction: column; gap: 10px; padding: 14px 20px; background: #0f172a; border-top: 1px solid #334155; }
.pqb-import-radio { display: flex; align-items: center; gap: 8px; color: #cbd5e1; font-size: 0.84rem; cursor: pointer; }
.pqb-import-radio input[type="radio"] { accent-color: #818cf8; width: 15px; height: 15px; cursor: pointer; }
.pqb-import-footer { display: flex; justify-content: flex-end; gap: 10px; padding: 14px 20px; background: #0f172a; border-top: 1px solid #334155; }
</style>

<!-- Reglas globales que afectan a los componentes de pregunta dentro del card-body -->
<style>
.pqb-card-body select { width: auto !important; min-width: 110px; max-width: 220px; }
.pqb-card-body input[type="number"] { max-width: 160px; }
.pqb-card-body .q-wrap { max-width: 760px; }
</style>
